/**
 * Destroy command - destroys a specific session
 *
 * Accepts a session ID or name from the local registry.
 */

import chalk from 'chalk';
import { SANDBOX_WORKER_URL } from '@spacecode/shared/remote';
import { resolveAuthToken, sandboxClientOptions } from '../config.js';
import { loadSessions, resolveSession, markSessionDestroyed } from '../sessions.js';

export async function destroy(idOrName: string): Promise<void> {
  console.log(chalk.bold('\nDestroying session...\n'));

  // ── Resolve name to ID ────────────────────────────────────────────────

  let sessionId = idOrName;
  const sessions = await loadSessions();
  const session = resolveSession(sessions, idOrName);

  if (session) {
    sessionId = session.id;
    const label = session.name ? `"${session.name}" (${session.id})` : session.id;
    console.log(chalk.dim(`Session: ${label}`));
  }

  // ── Auth & client ─────────────────────────────────────────────────────

  const token = await resolveAuthToken();
  const { createSandboxClient, SandboxApiError } = await import('@spacecode/sandbox-client');
  const client = createSandboxClient(SANDBOX_WORKER_URL, token, sandboxClientOptions(token));

  // Check session status first
  try {
    const info = await client.getSandboxStatus(sessionId);
    console.log(chalk.dim(`Session ${sessionId} is currently: ${info.status}`));
  } catch (error) {
    if (error instanceof SandboxApiError && error.status === 404) {
      // Still clean up local registry even if session is already gone
      await markSessionDestroyed(sessionId).catch((err) => {
      console.warn(chalk.dim(`Warning: Could not update local session registry: ${err instanceof Error ? err.message : String(err)}`));
    });
      throw new Error(`Session '${idOrName}' not found. Run \`spacecode list\` to see available sessions.`);
    }
    console.log(chalk.dim(`Could not check session status: ${error instanceof Error ? error.message : String(error)}`));
  }

  // Destroy the session
  console.log(`Destroying session ${chalk.cyan(sessionId)}...`);

  try {
    await client.destroySandbox(sessionId);
    await markSessionDestroyed(sessionId).catch((err) => {
      console.warn(chalk.dim(`Warning: Could not update local session registry: ${err instanceof Error ? err.message : String(err)}`));
    });
    console.log(chalk.green(`\nSession destroyed successfully.`));
  } catch (error) {
    throw new Error(`Failed to destroy session: ${error instanceof Error ? error.message : String(error)}`);
  }
}
